/**
 * HistoricoPage — Salidas pasadas (completadas / canceladas / suspendidas).
 * Solo lectura. Útil para reportes y consultas históricas.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, TextField, MenuItem, Chip, LinearProgress,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import DataTable from '../common/DataTable';
import EmptyState from '../common/EmptyState';
import useToast from '../common/useToast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
function getEmpresaId() { try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; } catch { return '1'; } }
function authHeaders() { const t = localStorage.getItem('token'); return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }; }
function fmtFecha(s) { if (!s) return '—'; try { return new Date(s).toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }); } catch { return s; } }

const ESTADO_COLORS = {
  completada: { bg: '#dcfce7', color: '#166534', label: '✓ Completada' },
  cancelada:  { bg: '#fee2e2', color: '#991b1b', label: '✗ Cancelada' },
  suspendida: { bg: '#fef3c7', color: '#92400e', label: '⚠ Suspendida' },
  programada: { bg: '#e0f2fe', color: '#075985', label: 'Programada (pasada)' },
};

export default function HistoricoPage() {
  const empresaId = getEmpresaId();
  const { toast, ToastContainer } = useToast();
  const [salidas, setSalidas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/salidas?empresa_id=${empresaId}`, { headers: authHeaders() });
      const data = await r.json();
      const list = Array.isArray(data) ? data : (data.salidas || []);
      const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
      // Histórico: salidas pasadas (fecha < hoy) o canceladas
      setSalidas(list.filter(s => {
        const f = s.fecha ? new Date(s.fecha) : null;
        return (f && f < hoy) || s.estado === 'cancelada' || s.estado === 'completada';
      }).sort((a, b) => new Date(b.fecha) - new Date(a.fecha)));
    } catch (e) { toast.error('Error cargando'); }
    setLoading(false);
  }, [empresaId]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return salidas.filter(s => {
      if (filterEstado && s.estado !== filterEstado) return false;
      if (q) {
        const txt = `${s.paquete_nombre || ''}`.toLowerCase();
        if (!txt.includes(q)) return false;
      }
      return true;
    });
  }, [salidas, search, filterEstado]);

  const columns = [
    { id: 'fecha', label: 'Fecha', sortable: true, sortValue: (r) => r.fecha,
      render: (r) => <Typography sx={{ fontSize: 12 }}>{fmtFecha(r.fecha)} · {r.hora_salida?.slice(0,5)}</Typography> },
    { id: 'paquete', label: 'Paquete', sortable: true, sortValue: r => r.paquete_nombre || '',
      render: (r) => <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{r.paquete_nombre}</Typography> },
    { id: 'ocupacion', label: 'Ocupación final', sortable: true,
      sortValue: (r) => (r.asientos_reservados || 0),
      render: (r) => {
        const pct = (r.capacidad_total || 0) > 0 ? ((r.asientos_reservados || 0) / r.capacidad_total) * 100 : 0;
        return (
          <Box sx={{ minWidth: 100 }}>
            <Typography sx={{ fontSize: 11 }}>{r.asientos_reservados || 0}/{r.capacidad_total || 0} ({pct.toFixed(0)}%)</Typography>
            <LinearProgress variant="determinate" value={Math.min(pct, 100)}
              sx={{ height: 3, borderRadius: 2, mt: 0.5,
                     bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#94a3b8' } }} />
          </Box>
        );
      },
    },
    { id: 'estado', label: 'Estado',
      render: (r) => {
        const c = ESTADO_COLORS[r.estado] || ESTADO_COLORS.programada;
        return <Chip size="small" label={c.label} sx={{ bgcolor: c.bg, color: c.color, fontSize: 10, height: 20, fontWeight: 700 }} />;
      } },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Histórico de salidas</Typography>
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
          Salidas pasadas: completadas, canceladas o suspendidas. Solo lectura.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 1, mb: 2 }}>
        <TextField size="small" placeholder="Buscar paquete…" value={search} onChange={e => setSearch(e.target.value)} />
        <TextField size="small" select label="Estado" value={filterEstado} onChange={e => setFilterEstado(e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          {Object.entries(ESTADO_COLORS).map(([k, c]) => <MenuItem key={k} value={k}>{c.label}</MenuItem>)}
        </TextField>
      </Box>

      {filteredRows.length === 0 && !loading ? (
        <EmptyState icon={<HistoryIcon sx={{ fontSize: 56, color: '#cbd5e1' }} />}
          title="Sin histórico" message="Aún no hay salidas pasadas registradas." />
      ) : (
        <DataTable columns={columns} rows={filteredRows} loading={loading} rowKey={(r) => r.id}
          defaultSortBy="fecha" defaultSortDir="desc" />
      )}

      <ToastContainer />
    </Box>
  );
}
